/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { propTypes, TRAVEL_EXPERT_USER_TYPE } from '../../../../util/types';
import { ensureCurrentUser } from '../../../../util/data';

import {
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';
import { useSelector } from 'react-redux';
import { userTypeSelector } from '../../../../ducks/user.duck';

const cmsPages = [
  { pageId: 'how_it_works', messageId: 'TopbarDesktop.howWorks' },
  { pageId: 'top_destiations', messageId: 'TopbarDesktop.topDestinations' },
  { pageId: 'travel_tips_and_recommendations', messageId: 'TopbarDesktop.travelTips' },
  { pageId: 'travel_sustainably', messageId: 'TopbarDesktop.travelSustainably' },
  { pageId: 'travel_products_and_essentials', messageId: 'TopbarDesktop.travelProducts' },
  { pageId: 'our_partnerships', messageId: 'TopbarDesktop.ourPartnerships' },
];

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    notificationCount,
    customLinks,
    onLogout,
    params={},
    inboxNotificationCount
  } = props;

  const userType = useSelector(userTypeSelector);
  const user = ensureCurrentUser(currentUser);

  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    return (
      <div className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>

          <div className={css.customLinksWrapper}>
            {cmsPages.map(({ pageId, messageId }) => (
              <NamedLink
                name="CMSPage"
                className={classNames(css.navigationLink)}
                params={{ pageId }}
              >
                <span className={css.menuItemBorder} />
                <FormattedMessage id={messageId} />
              </NamedLink>
            ))}
            {/* {extraLinks} */}
          </div>

          <div className={css.spacer} />
        </div>
        <div className={css.footer}>
          <NamedLink
            className={css.createNewListingLink}
            name="SignupForUserTypePage"
            params={{ userType: TRAVEL_EXPERT_USER_TYPE }}
          >
            <FormattedMessage id="TopbarMobileMenu.becomeTravelExpert" />
          </NamedLink>
        </div>
      </div>
    );
  }

  const notificationCountBadge =
    inboxNotificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={inboxNotificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };
  const inboxTab = currentUserHasListings ? 'inquiries' : 'orders';

  const accountLinks = [
    {
      name: "ProfileSettingsPage",
      className: classNames(css.navigationLink, currentPageClass("ProfileSettingsPage")),
      labelId: "TopbarMobileMenu.profileSettingsLink",
    },
    {
      name: "AccountSettingsPage",
      className: classNames(css.navigationLink, currentPageClass("AccountSettingsPage")),
      labelId: "TopbarMobileMenu.accountSettingsLink",
    },
    ...(userType === TRAVEL_EXPERT_USER_TYPE
      ? [
        {
          name: "ManageListingsPage",
          className: classNames(css.navigationLink, currentPageClass("ManageListingsPage")),
          labelId: "TopbarMobileMenu.yourListingsLink",
        },
      ]
      : []),
    {
      name: "InboxPage",
      params: { tab: inboxTab },
      className: classNames(css.inbox, currentPageClass(`InboxPage:${inboxTab}`)),
      labelId: "TopbarMobileMenu.inboxLink",
      extraContent: notificationCountBadge,
    },
  ];

    const currentCMSPageClass = pageId => (pageId === params?.pageId ? css.currentPage : null);
  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        <div className={css.accountLinksWrapper}>
          {accountLinks.map(({ name, params = {}, className, labelId, extraContent = "" }, index) => (
            <NamedLink key={index} className={className} name={name} params={params}>
              <FormattedMessage id={labelId} />
              {extraContent}
            </NamedLink>
          ))}
           {cmsPages.map(({ pageId, messageId }) => (
              <NamedLink
                name="CMSPage"
                className={classNames(css.navigationLink,currentCMSPageClass(pageId))}
                params={{ pageId }}
              >
                <span className={css.menuItemBorder} />
                <FormattedMessage id={messageId} />
              </NamedLink>
            ))}
        </div>

        <div className={css.customLinksWrapper}>{extraLinks}</div>
        <div className={css.spacer} />
      </div>
      <div className={css.footer}>
        <NamedLink name="SearchPage" className={css.createNewListingLink}>
          <FormattedMessage id="TopbarMobileMenu.discoverTravelExperts" />
        </NamedLink>
      </div>
    </div>
  );
};

TopbarMobileMenu.defaultProps = { currentUser: null, notificationCount: 0, currentPage: null };

const { bool, func, number, string } = PropTypes;

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
};

export default TopbarMobileMenu;
